import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchSalesForSelect, type SaleOption } from '@/services/lookup';
import { fetchVariants, type VariantOption } from '@/services/variants';
import { createWarranty } from '@/services/warranties';

interface WarrantyModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function WarrantyModal({ onClose, onSaved }: WarrantyModalProps) {
  const [sales, setSales] = useState<SaleOption[]>([]);
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const [saleId, setSaleId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('90');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSalesForSelect().then((r) => {
      setSales(r);
      if (r[0]) setSaleId(r[0].id);
    });
    fetchVariants().then((r) => setVariants(r.data));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!saleId) {
      setError('Selecciona la venta original.');
      return;
    }
    if (!variantId) {
      setError('Selecciona el producto.');
      return;
    }
    if (!reason.trim()) {
      setError('Describe el motivo del reclamo.');
      return;
    }
    const sale = sales.find((s) => s.id === saleId);
    if (!sale?.customerId) {
      setError('No se pudo identificar al cliente de la venta.');
      return;
    }

    setSaving(true);
    const res = await createWarranty({
      saleId,
      customerId: sale.customerId,
      variantId,
      reason,
      days: Math.max(1, Number(days) || 90),
    });
    setSaving(false);

    if (!res.success) {
      setError(res.error || 'No se pudo crear el reclamo.');
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal title="Nuevo Reclamo" subtitle="Registra un reclamo de garantía." onClose={onClose} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Venta original *</label>
          <select className={inputCls} value={saleId} onChange={(e) => setSaleId(e.target.value)}>
            {sales.map((s) => (
              <option key={s.id} value={s.id}>
                {s.saleNumber} · {s.customerName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Producto *</label>
          <select className={inputCls} value={variantId} onChange={(e) => setVariantId(e.target.value)}>
            <option value="">Selecciona variante...</option>
            {variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.productName} · {v.sizeName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Vigencia de garantía (días)</label>
          <input type="number" min="1" className={inputCls} value={days} onChange={(e) => setDays(e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>Motivo del reclamo *</label>
          <textarea className={`${inputCls} resize-none`} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Se descosió la costura, suela despegada, etc." />
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear reclamo</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}